import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/app/environments/environment';
import {
  AnalyticsIntegrationResponse,
  AnalyticsIntegrationState,
  AnalyticsUserResponse,
  AnalyticsUserState,
  ConnectIntegrationCommand,
  ConnectIntegrationResponse,
  CreateDashboardMappingCommand,
  DashboardEmbedConfigResponse,
  DashboardEmbedState,
  DashboardMappingOrderItem,
  DashboardMappingResponse,
  DashboardMappingState,
  MetabaseCollectionItemResponse,
  ProvisioningStatusResponse,
  ProvisioningStatusState,
  UpdateDashboardMappingCommand,
  mapDashboardMappingResponseToState,
  mapEmbedConfigResponseToState,
  mapIntegrationResponseToState,
  mapProvisioningResponseToState,
  mapUserResponseToState,
} from '../models/MetabaseModels';

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private apiUrl = environment.companyApiUrl;

  constructor(private http: HttpClient) {}

  private isPlaceholderIntegrationResponse(response: AnalyticsIntegrationResponse): boolean {
    return (
      response.id <= 0 &&
      response.status === 0 &&
      !response.permissionGroupName?.trim() &&
      !response.collectionName?.trim()
    );
  }

  // ─── Integration ──────────────────────────────────────────────────────────

  getIntegrationStatus(companyId: number): Observable<AnalyticsIntegrationState | null> {
    const url = `${this.apiUrl}/Companies/${companyId}/Analytics/integration`;
    return this.http
      .get<AnalyticsIntegrationResponse>(url)
      .pipe(
        map((response) =>
          this.isPlaceholderIntegrationResponse(response)
            ? null
            : mapIntegrationResponseToState(response),
        ),
      );
  }

  connectIntegration(
    companyId: number,
    command: ConnectIntegrationCommand,
  ): Observable<ConnectIntegrationResponse> {
    const url = `${this.apiUrl}/Companies/${companyId}/Analytics/connect`;
    return this.http.post<ConnectIntegrationResponse>(url, command);
  }

  disconnectIntegration(companyId: number): Observable<void> {
    const url = `${this.apiUrl}/Companies/${companyId}/Analytics/disconnect`;
    return this.http.post<void>(url, {});
  }

  getProvisioningStatus(companyId: number): Observable<ProvisioningStatusState> {
    const url = `${this.apiUrl}/Companies/${companyId}/Analytics/provisioningstatus`;
    return this.http.get<ProvisioningStatusResponse>(url).pipe(map(mapProvisioningResponseToState));
  }

  // ─── Dashboards ───────────────────────────────────────────────────────────

  getDashboardMappings(companyId: number, appId?: number): Observable<DashboardMappingState[]> {
    let url = `${this.apiUrl}/Companies/${companyId}/Analytics/dashboards`;
    if (appId !== undefined && appId !== null) {
      url += `?appId=${appId}`;
    }
    return this.http
      .get<DashboardMappingResponse[]>(url)
      .pipe(map((items) => items.map(mapDashboardMappingResponseToState)));
  }

  createDashboardMapping(
    companyId: number,
    command: CreateDashboardMappingCommand,
  ): Observable<DashboardMappingState> {
    const url = `${this.apiUrl}/Companies/${companyId}/Analytics/dashboards`;
    return this.http
      .post<DashboardMappingResponse>(url, command)
      .pipe(map(mapDashboardMappingResponseToState));
  }

  updateDashboardMapping(
    companyId: number,
    mappingId: number,
    command: UpdateDashboardMappingCommand,
  ): Observable<void> {
    const url = `${this.apiUrl}/Companies/${companyId}/Analytics/dashboards/${mappingId}`;
    return this.http.put<void>(url, command);
  }

  deleteDashboardMapping(companyId: number, mappingId: number): Observable<void> {
    const url = `${this.apiUrl}/Companies/${companyId}/Analytics/dashboards/${mappingId}`;
    return this.http.delete<void>(url);
  }

  reorderDashboardMappings(
    companyId: number,
    items: DashboardMappingOrderItem[],
  ): Observable<void> {
    const url = `${this.apiUrl}/Companies/${companyId}/Analytics/dashboards/reorder`;
    return this.http.put<void>(url, { items });
  }

  assignDashboardPermission(
    companyId: number,
    mappingId: number,
    roleIds: number[],
  ): Observable<void> {
    const url = `${this.apiUrl}/Companies/${companyId}/Analytics/dashboards/${mappingId}/permissions`;
    return this.http.put<void>(url, { roleIds });
  }

  getMetabaseDashboards(companyId: number): Observable<MetabaseCollectionItemResponse[]> {
    const url = `${this.apiUrl}/Companies/${companyId}/Analytics/metabase-dashboards`;
    return this.http.get<MetabaseCollectionItemResponse[]>(url);
  }

  triggerHealthCheck(companyId: number): Observable<void> {
    const url = `${this.apiUrl}/Companies/${companyId}/Analytics/healthcheck`;
    return this.http.post<void>(url, {});
  }

  // ─── Users ────────────────────────────────────────────────────────────────

  getAnalyticsUsers(companyId: number): Observable<AnalyticsUserState[]> {
    const url = `${this.apiUrl}/Companies/${companyId}/Analytics/users`;
    return this.http
      .get<AnalyticsUserResponse[]>(url)
      .pipe(map((items) => items.map(mapUserResponseToState)));
  }

  createAnalyticsUser(companyId: number, name: string, email: string): Observable<void> {
    const url = `${this.apiUrl}/Companies/${companyId}/Analytics/users`;
    return this.http.post<void>(url, { name, email });
  }

  setAnalyticsUserStatus(companyId: number, userId: number, isActive: boolean): Observable<void> {
    const url = `${this.apiUrl}/Companies/${companyId}/Analytics/users/${userId}/status`;
    return this.http.put<void>(url, { isActive });
  }

  resetAnalyticsUserPassword(companyId: number, userId: number): Observable<void> {
    const url = `${this.apiUrl}/Companies/${companyId}/Analytics/users/${userId}/reset-password`;
    return this.http.post<void>(url, {});
  }

  resetAnalyticsUserStatus(companyId: number, userId: number): Observable<void> {
    const url = `${this.apiUrl}/Companies/${companyId}/Analytics/users/${userId}/reset-status`;
    return this.http.post<void>(url, {});
  }

  // ─── Embed ────────────────────────────────────────────────────────────────

  getDashboardEmbedUrl(companyId: number, dashboardId: string): Observable<DashboardEmbedState> {
    const url = `${this.apiUrl}/Companies/${companyId}/Analytics/embed/${dashboardId}`;
    return this.http
      .get<DashboardEmbedConfigResponse>(url)
      .pipe(map(mapEmbedConfigResponseToState));
  }

  // ─── Legacy aliases ───────────────────────────────────────────────────────
}
